import { useIntl } from "react-intl";

import {
    featureFlags,
    checkFeatureFlagGate
} from "@/utils/feature-flags/index.ts";

export interface CollectionPricingHeroMessages {
    home: string;
    heading: string;
    includes: string;
    goals: string;
    teams: string;
    studio: string;
    search: string;
    chat: string;
    analytics: string;
    admin: string;
}

export function useCollectionPricingHeroMessages(): CollectionPricingHeroMessages {
    const intl = useIntl();

    const hardcoded = checkFeatureFlagGate(
        featureFlags.gwp8279CollectionHeroIntl
    );
    if (hardcoded) {
        return getHardcodedMessages(intl.locale);
    }

    return {
        heading: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.platformAppsHeading",
            defaultMessage: "Plus all Atlassian Platform Apps"
        }),
        includes: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.includes",
            defaultMessage: "Includes"
        }),
        home: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.home.alt",
            defaultMessage: "Home"
        }),
        goals: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.goals",
            defaultMessage: "Goals"
        }),
        teams: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.teams",
            defaultMessage: "Teams"
        }),
        studio: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.studio",
            defaultMessage: "Studio"
        }),
        search: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.search",
            defaultMessage: "Search"
        }),
        chat: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.chat",
            defaultMessage: "Chat"
        }),
        analytics: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.analytics",
            defaultMessage: "Analytics"
        }),
        admin: intl.formatMessage({
            id: "TeamworkCollectionPricingPageHero.app.admin",
            defaultMessage: "Admin"
        })
    };
}

export function getHardcodedMessages(
    locale: string
): CollectionPricingHeroMessages {
    return MESSAGES[locale] ?? enMessages;
}

const enMessages = {
    home: "Home",
    heading: "Plus all Atlassian Platform Apps",
    includes: "Includes",
    goals: "Goals",
    teams: "Teams",
    studio: "Studio",
    search: "Search",
    chat: "Chat",
    analytics: "Analytics",
    admin: "Admin"
};

const MESSAGES: Record<string, CollectionPricingHeroMessages> = {
    en: enMessages,
    nl: {
        home: "Home",
        heading: "Plus alle Atlassian Platform-apps",
        includes: "Omvat",
        goals: "Doelstellingen",
        teams: "Teams",
        studio: "Studio",
        search: "Zoeken",
        chat: "Chat",
        analytics: "Analyses",
        admin: "Beheerder"
    },
    es: {
        home: "Inicio",
        heading:
            "Además de todas las aplicaciones de la plataforma de Atlassian",
        includes: "Incluye",
        goals: "Objetivos",
        teams: "Equipos",
        studio: "Studio",
        search: "Buscar",
        chat: "Chat",
        analytics: "Análisis",
        admin: "Administrativo"
    },
    ru: {
        home: "Главная",
        heading: "А также все приложения платформы Atlassian",
        includes: "Включает",
        goals: "Цели",
        teams: "Команды",
        studio: "Мастерская",
        search: "Поиск",
        chat: "Чат",
        analytics: "Аналитика",
        admin: "Администратор"
    },
    fr: {
        home: "Accueil",
        heading: "Et toutes les apps de la plateforme Atlassian",
        includes: "Fonctionnalités incluses",
        goals: "Objectifs",
        teams: "Équipes",
        studio: "Studio",
        search: "Recherche",
        chat: "Chat",
        analytics: "Analyses",
        admin: "Administrateur"
    },
    ko: {
        home: "홈",
        heading: "및 모든 Atlassian 플랫폼 앱",
        includes: "포함 사항",
        goals: "목표",
        teams: "팀",
        studio: "스튜디오",
        search: "검색",
        chat: "채팅",
        analytics: "분석",
        admin: "관리자"
    },
    br: {
        home: "Início",
        heading: "Além de todos os apps da plataforma Atlassian",
        includes: "Inclui",
        goals: "Metas",
        teams: "Equipes",
        studio: "Studio",
        search: "Pesquisar",
        chat: "Bate-papo",
        analytics: "Dados de Análise",
        admin: "Admin"
    },
    pl: {
        home: "Strona główna",
        heading: "Plus wszystkie aplikacje platformy Atlassian",
        includes: "Obejmuje",
        goals: "Cele",
        teams: "Zespoły",
        studio: "Studio",
        search: "Wyszukiwanie",
        chat: "Czat",
        analytics: "Analizy",
        admin: "Administrator"
    },
    de: {
        home: "Home",
        heading: "Plus alle Apps der Atlassian-Plattform",
        includes: "Beinhaltet",
        goals: "Ziele",
        teams: "Teams",
        studio: "Studio",
        search: "Suchen",
        chat: "Chat",
        analytics: "Analysen",
        admin: "Administrator"
    },
    zh: {
        home: "首页",
        heading: "以及所有 Atlassian 平台应用",
        includes: "包括",
        goals: "目标",
        teams: "团队",
        studio: "工作室",
        search: "搜索",
        chat: "聊天",
        analytics: "分析",
        admin: "管理员"
    },
    it: {
        home: "Pagina iniziale",
        heading: "E tutte le app della piattaforma di Atlassian",
        includes: "Include",
        goals: "Obiettivi",
        teams: "Team",
        studio: "Studio",
        search: "Cerca",
        chat: "Chat",
        analytics: "Analisi",
        admin: "Amministratore"
    },
    ja: {
        home: "ホーム",
        heading: "さらにすべてのアトラシアン プラットフォーム アプリ",
        includes: "インクルード",
        goals: "目標",
        teams: "チーム",
        studio: "スタジオ",
        search: "検索",
        chat: "チャット",
        analytics: "分析機能",
        admin: "管理者"
    }
};
